import type {CSSProperties, SyntheticEvent} from 'react';
import React from 'react'
import type { StopPosition} from './Stop';
import {DraggableStop} from './Stop'
import * as kb from '../kb'
import {columnName, formatMinutes} from '../kb'
import Icon from './Icon'
import icons from '../../markons/markons'
import calculateDistance, {convertDistance} from '../distance'
// import ContentSection from './ContentSection';
import SubSection from './SubSection'
import Moment from 'moment'
import {GridCol, GridRow, GridRowSingleCol, GridSection, SubGrid} from './grid-form'
import ToggleButton from './ToggleButton'
import FlightDetailsDialog from './FlightDetailsDialog'
import BodyEnd from './BodyEnd'
import AioSelect from './aio-inputs/AioSelect'
import AioDatePicker from './aio-inputs/AioDatePicker'
import AioText from './aio-inputs/AioText'
import AioTextArea from './aio-inputs/AioTextArea'
import AioTimePicker from './aio-inputs/AioTimePicker'
import {FlexGrow, FlexRow, FlexShrink} from './Flex'
import ErrorBoundary from './ErrorBoundary'
import type {
    DroppableProvided,
    DroppableStateSnapshot,
    DropResult,
    ResponderProvided
} from "@hello-pangea/dnd";
import {
    DragDropContext,
    Droppable
} from "@hello-pangea/dnd"
import {SegmentBagsDialog} from './SegmentBagsDialog'
import type {FlightInfo, SegmentBagsValue, SegmentData} from '../models/Segment'
import {TestId} from '../contexts/testid'
import _ from 'lodash'
import setIn from 'lodash/fp/set'
import {uuid} from '../util/random'
import {parseInt} from '../util/math'
import {makeGoogleMapsUrl} from '../google-maps'
import {ExternalLink} from './external-links'
import {InlineSpacer} from './spacer'

function formatDuration(date?: Moment.Moment, mins?: number, militaryTime=false) {
    if(!mins) return "— Not Selected —"
    const minsStr = formatMinutes(mins)
    if(!date) return minsStr
    const dateStr = date.clone().add(mins, 'minutes').format(militaryTime ? 'HHmm' : 'h:mm A')
    return `${dateStr} (${minsStr})`
}

interface State {
    calculating: boolean
    flightDialogOpen: boolean
    bagDialogOpen: boolean
    droppableId: string
}

interface SegmentProps {
    number: StringNum
    addStop: (stopIdx: number) => (ev: SyntheticEvent<unknown>) => void
    removeStop: (stopIdx: number) => (ev: SyntheticEvent<unknown>) => void
    removeSegment: (ev: SyntheticEvent<unknown>) => void
    value: SegmentData
    onChange: EventCallback<SegmentData>
    billingType: AnyDuringMigration
    distanceUnit: AnyDuringMigration
    quoteRequest?: boolean
    militaryTime: boolean
    googleMapsApiKey: string
    testId?: string
}

function getListStyle(isDraggingOver: boolean): CSSProperties {
    return {
        background: isDraggingOver ? "rgba(209,233,255,.5)" : undefined,
        // margin: `8px 0 0 8px`,
        // padding: grid,
        // width: 250
    }
}

const DURATION_MINS = _.range(0, 1441, 5)
const DURATION_SET = new Set(DURATION_MINS)

function notZero(x: undefined | null | number | string) {
    return !!x && x !== '0'
}

export default class Segment extends React.Component<SegmentProps, State> {

    state = {
        calculating: false,
        flightDialogOpen: false,
        bagDialogOpen: false,
        droppableId: uuid(),
    }

    // TODO: make middle section disappear with media suery < ~400px
    // figure out how do flex basis properly without "100"

    render() {
        const {number, addStop, removeStop, removeSegment, value, onChange, billingType, distanceUnit, quoteRequest, militaryTime, testId} = this.props


        const durationMins = parseInt(value.duration, 0)

        let date: undefined | Moment.Moment
        if(value.date && value.time) {
            date = Moment(`${value.date} ${value.time}`, 'YYYY-MM-DD h:mm A')
        }

        const stops = _.get(value, 'stops', [])

        // const waypoints = stops.map(s => formatAddress(s))
        // let origin = ''
        // let destination = '';
        // if(waypoints.length) {
        //     origin = waypoints.shift()!
        //     if(waypoints.length) {
        //         destination = waypoints.pop()!
        //     }
        // }
        // const googleMapsUrl = appendQueryParams('https://www.google.com/maps/dir/', {
        //     api: 1,
        //     travelmode: 'driving',
        //     origin,
        //     destination,
        //     waypoints: waypoints.join('|')
        // })
        // const googleMapsUrl = makeGoogleMapsUrl(value)

        return (
            <TestId prefix={testId || `segment-${number}`}>
                <SubSection title={`Segment ${number}`} remove={removeSegment}>
                    <ErrorBoundary>
                        <GridSection>
                            <GridRow>
                                <GridCol minWidth="130px">
                                    <AioDatePicker required label="Date" testId="date" {...this.linkState('date', date => date)}/>
                                </GridCol>
                                <GridCol minWidth="110px">
                                    <AioTimePicker required label="Start Time" testId="start-time" {...this.linkState('time', ev => ev.value)}
                                                   militaryTime={militaryTime}/>
                                    {/*<label htmlFor={`segment-${number}-time`}>Start Time</label>*/}
                                    {/*<TimePicker {...this.linkState('time')}/>*/}
                                </GridCol>
                                <GridCol minWidth="190px">
                                    <AioSelect testId="duration"
                                        label={date ? "End Time (Duration)" : "Duration"} {...this.linkState('duration')}>
                                        {!DURATION_SET.has(durationMins) ? <option value={durationMins}>{formatDuration(date, durationMins, militaryTime)}</option> : null}
                                        {DURATION_MINS.map((mins: number) => <option key={mins} value={mins}>{formatDuration(date, mins, militaryTime)}</option>)}
                                    </AioSelect>
                                </GridCol>
                                {billingType === 'DISTANCE' || billingType === 'DISTANCE_TOTAL' ?
                                    <GridCol minWidth="180px">
                                        <FlexRow gap={5}>
                                            <FlexGrow>
                                                <AioText label="Distance" {...this.linkState('distance')}
                                                         disabled={this.state.calculating}/>
                                            </FlexGrow>
                                            <FlexShrink>
                                                {distanceUnit}
                                            </FlexShrink>
                                            <FlexShrink>
                                                <button className="btn btn-new" onClick={this.calculateDistance}
                                                        disabled={this.state.calculating}
                                                        title="Calculate total driving distance for this segment from Google Maps">
                                                    <Icon name={icons.miscMapMarker}/> Calc
                                                </button>
                                            </FlexShrink>
                                        </FlexRow>
                                    </GridCol> : null}


                                <GridCol shrink>
                                    <ToggleButton
                                        checked={Boolean(value.flight && (value.flight.domestic || value.flight.departure || value.flight.number))}
                                        title="Click to add flight details"
                                        onClick={() => this.setState({flightDialogOpen: true})}>
                                        <Icon
                                            name={value.flight && value.flight.departure === 'DEPARTURE' ? icons.faPlaneDeparture : (value.flight && value.flight.departure === 'ARRIVAL' ? icons.faPlaneArrival : icons.faPlane)}/>
                                    </ToggleButton>

                                    {this.state.flightDialogOpen && <BodyEnd><FlightDetailsDialog
                                        close={() => this.setState({flightDialogOpen: false})} {...this.linkState<FlightInfo,[FlightInfo]>('flight', _.identity)}/></BodyEnd>}

                                    <ToggleButton
                                        checked={Boolean(value.bags && (notZero(value.bags.small) || notZero(value.bags.medium) || notZero(value.bags.large)))}
                                        title="Click to add baggage details"
                                        onClick={() => this.setState({bagDialogOpen: true})}>
                                        <Icon name={icons.faSuitcaseRolling}/>
                                    </ToggleButton>

                                    {this.state.bagDialogOpen && <BodyEnd><SegmentBagsDialog
                                        close={() => this.setState({bagDialogOpen: false})} {...this.linkState<SegmentBagsValue,[SegmentBagsValue]>('bags', _.identity)}/></BodyEnd>}

                                    {!quoteRequest &&
                                        <ToggleButton {...this.linkToggleButton('transfer')}>
                                            <Icon name={icons.faRoute}
                                                  title={"Enabling this toggle switch adds \"(Direct Transfer)\" immediately\nfollowing the scheduled time frame, in addition to only showing\nthe start time on all customer communications pertaining\nspecifically to this segment."}/>
                                        </ToggleButton>
                                    }
                                </GridCol>
                            </GridRow>

                            <DragDropContext onDragEnd={this.handleDragEnd}>
                                <Droppable droppableId={this.state.droppableId}>
                                    {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
                                        <SubGrid
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            style={getListStyle(snapshot.isDraggingOver)}
                                        >
                                            {stops.map((stop: any, i: number) => {
                                                let stopPos: StopPosition = 'ST'
                                                if(i === 0) {
                                                    stopPos = 'PU'
                                                } else if(i === stops.length - 1) {
                                                    stopPos = 'DO'
                                                }
                                                return <DraggableStop key={stop.key} draggableId={stop.key} index={i} {...this.linkState(`stops.${i}`, _.identity)} position={stopPos}
                                                                      number={number} letter={columnName(i)} addStop={addStop(i + 1)}
                                                                      removeStop={removeStop(i)} googleMapsApiKey={this.props.googleMapsApiKey}/>
                                            })}
                                            {provided.placeholder}
                                        </SubGrid>
                                    )}
                                </Droppable>
                            </DragDropContext>

                            <GridRowSingleCol>
                                <InlineSpacer width={42}/>
                                <ExternalLink className="google-maps-preview-link" href={makeGoogleMapsUrl(value)}>
                                    <img src="/img/google-maps-icon.png"
                                        srcSet="/img/google-maps-icon.png 1x, /img/google-maps-icon-2x.png 2x"
                                        width="12" height="12"
                                        alt="" />
                                    Preview on Google Maps
                                </ExternalLink>
                            </GridRowSingleCol>

                            <GridRowSingleCol>
                                <AioTextArea label="Comments" testId="comments" {...this.linkState('comments')}/>
                            </GridRowSingleCol>
                        </GridSection>
                    </ErrorBoundary>
                </SubSection>
            </TestId>
        )
    }

    calculateDistance = (ev: any) => {
        ev.preventDefault()
        const stops = _.get(this.props.value, 'stops', [])
        this.setState({calculating: true})
        calculateDistance(stops, this.props.googleMapsApiKey)
            .then(dist => {
                this.props.onChange(setIn('distance', convertDistance(dist, this.props.distanceUnit), this.props.value))
            })
            .finally(() => {
                this.setState({calculating: false})
            })
    }

    handleDragEnd = (result: DropResult, provided: ResponderProvided) => {
        if(!result.destination) {
            // dropped outside the list
            return
        }

        const stops = Array.from(_.get(this.props.value, 'stops', []))
        const [removed] = stops.splice(result.source.index, 1)
        stops.splice(result.destination.index, 0, removed)

        this.props.onChange(setIn('stops', stops, this.props.value))
    }

    linkState = <Value=string, EventArgs extends any[]=[React.ChangeEvent<HTMLInputElement>]>(name: string, valueGetter: ValueGetter<Value,EventArgs> = DEFAULT_INPUT_GETTER) => {
        return {
            value: _.get(this.props.value, name, null) as Value,
            onChange: (...args: EventArgs) => {
                const value = valueGetter(...args)
                // console.log('seg change',name,value,setIn(name, value, this.props.value))
                this.props.onChange(setIn(name, value, this.props.value))
            },
        }
    }

    linkToggleButton = (name: string) => {
        return {
            checked: kb.toBool(_.get(this.props.value, name), false),
            onClick: () => {
                const value = !kb.toBool(_.get(this.props.value, name))
                this.props.onChange(setIn(name, value, this.props.value))
            },
        }
    }
}

// const defaultValueGetter: ValueGetter<string,[React.ChangeEvent<HTMLInputElement>]> = ev => ev.target.value
const DEFAULT_INPUT_GETTER: AnyFn = ev => ev.target.value

type ValueGetter<Value=string, EventArgs extends any[]=[React.ChangeEvent<HTMLInputElement>]> = (...args: EventArgs) => Value
